import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { useLanguageSetting } from 'common/hooks/useLanguageSetting';
import { DomainContext } from 'common/layouts/LayoutContext';
import Languages from 'common/utils/language-utils/index';
import { useLocation } from 'react-router-dom';
import SidebarToggle from '../../components/SidebarToggle';
import { useSidebar } from '../../contexts/SidebarContext';
import useFeatures from '../../hooks/useFeatures';
import NavData from './constants/NavData';

import {
  getAreAnyAssessmentPeriodsClosed,
  getCurrentOrganization,
  getCurrentUserMemberType,
  isCurrentMemberOrgAdmin,
  isCurrentUserStaffOrSuperAdmin,
  isCurrentUserSuperUser,
  getCurrentOrganizationMember,
  MEMBER_ROLE,
} from '../../selectors/auth';
import { useApiPortalV1AssessmentsPlansOrganizationLeaderAssignedRetrieveQuery } from 'areas/assessment-schedule/api';
import { RecommendationType } from 'common/constants/RecommendationType';
import NavItem from '../NavItem';
import { Bar } from './Bar';
import { cultureIcon, dashboardIcon } from './Icons';
import { filterMainNavData } from './utils/NavUtils';

const ORG_ADMIN_SIDEBAR_MODE_KEY = 'orgAdminSidebarMode';
type OrgAdminSidebarMode = 'organization' | 'teams';

export const isStrictCultureHubSidebarItem = (sidebarItem: any) =>
  Boolean(
    (sidebarItem.link || '').startsWith('/app/culture-hub') ||
    (sidebarItem.id || '').startsWith('culture-hub-')
  );

export const isSharedCultureHubSidebarItem = (sidebarItem: any) =>
  sidebarItem.id === 'knowledge-base' ||
  sidebarItem.id === 'glossary' ||
  sidebarItem.id === 'leaderboard' ||
  sidebarItem.id === 'summary';

export const isGuidedSummarySidebarItem = (sidebarItem: any) =>
  sidebarItem.id === 'summary' || sidebarItem.link === '/app/summary';

export const shouldIncludeOrgAdminNavItem = (sidebarItem: any, isInCultureHubMode: boolean) => {
  if (isGuidedSummarySidebarItem(sidebarItem)) {
    return isInCultureHubMode;
  }

  if (!isInCultureHubMode) {
    return !isStrictCultureHubSidebarItem(sidebarItem);
  }

  return (
    isStrictCultureHubSidebarItem(sidebarItem) ||
    isSharedCultureHubSidebarItem(sidebarItem)
  );
};

const SideBar = () => {
  const location = useLocation();
  const Domain = useContext(DomainContext);
  const language_hook = new Languages(useLanguageSetting());
  const { isOpen } = useSidebar();

  const isStaff = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isSuperuser = useSelector(isCurrentUserSuperUser);
  const memberType = useSelector(getCurrentUserMemberType);
  const areAnyAssessmentPeriodsClosed = useSelector(getAreAnyAssessmentPeriodsClosed);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);

  const { features } = useFeatures();
  const currentEmployee = useSelector(getCurrentOrganizationMember);
  const currentOrganization = useSelector(getCurrentOrganization);
  const hasEmployeeId = Boolean(currentEmployee && (currentEmployee as any).id);
  const organizationOptions = useMemo(() => {
    const rawOptions =
      (currentOrganization as any)?.organizationOptions ??
      (currentOrganization as any)?.organization_options ??
      [];

    const pulseOption = rawOptions.find(
      (option: any) => option?.key === 'pulse_surveys' || option?.key === 'pulse-surveys'
    );

    if (!pulseOption) {
      return [...rawOptions, { key: 'pulse_surveys', enabled: false, config: {} }];
    }

    if (pulseOption.key === 'pulse-surveys') {
      return rawOptions.map((option: any) =>
        option?.key === 'pulse-surveys'
          ? { ...option, key: 'pulse_surveys' }
          : option
      );
    }

    return rawOptions;
  }, [currentOrganization]);

  const isStrictCultureHubRoute = (pathname: string) => pathname.startsWith('/app/culture-hub');

  const isSharedCultureHubRoute = (pathname: string) =>
    pathname === '/app/leaderboard' ||
    pathname === '/app/glossary' ||
    pathname.startsWith('/app/knowledge-base') ||
    pathname === '/app/summary';

  const [orgAdminSidebarMode, setOrgAdminSidebarMode] = useState<OrgAdminSidebarMode>(() => {
    if (typeof window !== 'undefined') {
      const storedMode = window.sessionStorage.getItem(ORG_ADMIN_SIDEBAR_MODE_KEY);
      if (storedMode === 'organization' || storedMode === 'teams') {
        return storedMode;
      }
    }

    return isStrictCultureHubRoute(location.pathname) ? 'teams' : 'organization';
  });

  useEffect(() => {
    if (!isOrgAdmin) {
      return;
    }

    const onStrictCultureHubRoute = isStrictCultureHubRoute(location.pathname);
    const onSharedCultureHubRoute = isSharedCultureHubRoute(location.pathname);

    if (onStrictCultureHubRoute && orgAdminSidebarMode !== 'teams') {
      setOrgAdminSidebarMode('teams');
      return;
    }

    // Shared routes inherit the active mode; they do not force a context switch.
    if (!onStrictCultureHubRoute && !onSharedCultureHubRoute && orgAdminSidebarMode !== 'organization') {
      setOrgAdminSidebarMode('organization');
    }
  }, [isOrgAdmin, location.pathname, orgAdminSidebarMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isOrgAdmin) {
      window.sessionStorage.removeItem(ORG_ADMIN_SIDEBAR_MODE_KEY);
      return;
    }

    window.sessionStorage.setItem(ORG_ADMIN_SIDEBAR_MODE_KEY, orgAdminSidebarMode);
  }, [isOrgAdmin, orgAdminSidebarMode]);

  const isInCultureHub = isOrgAdmin
    ? orgAdminSidebarMode === 'teams'
    : isStrictCultureHubRoute(location.pathname) || isSharedCultureHubRoute(location.pathname);

  const shouldShowOrgAdminCultureHubSwitch = isOrgAdmin;
  
  // Use the new leader-assigned endpoint which returns the number of plans the leader is assigned to
  const { data: leaderAssigned } = useApiPortalV1AssessmentsPlansOrganizationLeaderAssignedRetrieveQuery(
    {
      leaderId: currentEmployee?.id || 0,
      typeOf: RecommendationType.TEAM_RECOMMENDATIONS,
    },
    { skip: !(memberType === MEMBER_ROLE.TEAM_LEADER && !!currentEmployee?.id) }
  );

  // Show the Team Projects menu only when API returns count > 0
  const leaderHasAssignedProjects = Boolean(
    leaderAssigned && typeof (leaderAssigned as any).count === 'number' && (leaderAssigned as any).count > 0
  );

  const navData = useMemo(() => {
    const filteredNav = filterMainNavData(
      NavData,
      isSuperuser,
      isStaff,
      memberType,
      features,
      areAnyAssessmentPeriodsClosed,
      hasEmployeeId,
      leaderHasAssignedProjects,
      organizationOptions
    );

    if (!isOrgAdmin) {
      return filteredNav;
    }

    return filteredNav.filter(navItem =>
      shouldIncludeOrgAdminNavItem(navItem.sidebarItem, isInCultureHub)
    );
  }, [
    isSuperuser,
    isStaff,
    memberType,
    features,
    areAnyAssessmentPeriodsClosed,
    hasEmployeeId,
    leaderHasAssignedProjects,
    organizationOptions,
    isOrgAdmin,
    isInCultureHub,
  ]);

  const orgAdminCultureHubSwitch = useMemo(() => {
    if (!shouldShowOrgAdminCultureHubSwitch) return null;

    const isInCultureHubMode = orgAdminSidebarMode === 'teams';
    const switchLink = isInCultureHubMode ? '/app/dashboard' : '/app/culture-hub/analytics';
    const switchLabel = isInCultureHubMode ? 'My Organization' : 'My Teams';
    const switchIcon = isInCultureHubMode ? dashboardIcon : cultureIcon;

    return {
      isDisabled: false,
      sidebarItem: {
        id: 'org-admin-culture-hub-switch',
        link: switchLink,
        title: switchLabel,
        fr: switchLabel,
        svg: switchIcon,
        org_admin: true,
        regular_member: false,
        team_admin: false,
        director: false,
        superuser: false,
        staff: false,
        section_title: 'Workspace',
      },
    };
  }, [shouldShowOrgAdminCultureHubSwitch, orgAdminSidebarMode]);

  return (
    <SidebarContainer isOpen={isOpen} data-testid="sidebar">
      <Bar isOpen={isOpen}>
        <LogoContainer isOpen={isOpen}>
          <Logo data-testid="logo" src={Domain.image} isOpen={isOpen} />
          <LogoText isOpen={isOpen}>nuLogic</LogoText>
        </LogoContainer>
        <NavScrollArea>
          {navData.map((nav, i) => (
            <NavItem
              key={i}
              navItem={nav.sidebarItem}
              isDisabled={nav.isDisabled}
              selected={location.pathname}
              isFrench={() => language_hook.is_french()}
              data-testid={`nav-item-${nav.sidebarItem.id}`}
              isOpen={isOpen}
            />
          ))}
          {orgAdminCultureHubSwitch && (
            <NavItem
              key={orgAdminCultureHubSwitch.sidebarItem.id}
              navItem={orgAdminCultureHubSwitch.sidebarItem}
              isDisabled={orgAdminCultureHubSwitch.isDisabled}
              selected={location.pathname}
              isFrench={() => language_hook.is_french()}
              data-testid={`nav-item-${orgAdminCultureHubSwitch.sidebarItem.id}`}
              isOpen={isOpen}
            />
          )}
        </NavScrollArea>
        <SidebarToggle />
      </Bar>
    </SidebarContainer>
  );
};

const SidebarContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 1001;
  transition: width 0.3s ease-in-out;
  width: ${props => (props.isOpen ? '250px' : '50px')};
`;

const LogoContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  padding: ${props => (props.isOpen ? '1px 20px' : '5px 0')};
  transition: all 0.3s ease-in-out;
  justify-content: center;
  width: 100%;
  gap: ${props => (props.isOpen ? '12px' : '0')};
`;

const Logo = styled.img<{ isOpen?: boolean }>`
  width: ${props => (props.isOpen ? '40px' : '32px')};
  height: ${props => (props.isOpen ? '40px' : '32px')};
  object-fit: contain;
  transition: all 0.3s ease-in-out;
  flex-shrink: 0;
`;

const LogoText = styled.div<{ isOpen: boolean }>`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => (props.isOpen ? '20px' : '16px')};
  font-weight: 600;
  color: #555;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  transition: all 0.3s ease-in-out;
  white-space: nowrap;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  flex-grow: 1;
`;

const NavScrollArea = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
`;

export default SideBar;
